/**
 * Error Recovery Utilities
 *
 * Provides comprehensive error handling and recovery mechanisms for data persistence operations.
 * Includes retry logic, error classification, and recovery strategies.
 */
/**
 * Error classification for different types of failures
 */
export var ErrorType;
(function (ErrorType) {
    ErrorType["NETWORK"] = "network";
    ErrorType["AUTHENTICATION"] = "authentication";
    ErrorType["AUTHORIZATION"] = "authorization";
    ErrorType["VALIDATION"] = "validation";
    ErrorType["SERVER"] = "server";
    ErrorType["TIMEOUT"] = "timeout";
    ErrorType["UNKNOWN"] = "unknown";
})(ErrorType || (ErrorType = {}));
/**
 * Check if device is online
 */
export const isOnline = () => {
    if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
        return navigator.onLine;
    }
    return true; // Assume online if we can't check
};
/**
 * Classify error type for appropriate handling
 */
export const classifyError = (error) => {
    if (!error)
        return ErrorType.UNKNOWN;
    const httpStatus = typeof error?.response?.status === 'number'
        ? error.response.status
        : typeof error?.status === 'number'
            ? error.status
            : undefined;
    // Network errors - check for Axios ERR_NETWORK and other network error codes
    if (error.code === 'NETWORK_ERROR' ||
        error.code === 'ERR_NETWORK' ||
        error.name === 'NetworkError' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('network') ||
        error.message?.includes('fetch') ||
        error.message?.includes('connection') ||
        (!error.response && error.request)) {
        return ErrorType.NETWORK;
    }
    // Authentication errors
    if (httpStatus === 401 ||
        error.message?.includes('unauthorized') ||
        error.message?.includes('authentication')) {
        return ErrorType.AUTHENTICATION;
    }
    // Authorization errors
    if (httpStatus === 403 ||
        error.message?.includes('forbidden') ||
        error.message?.includes('permission')) {
        return ErrorType.AUTHORIZATION;
    }
    // Validation errors
    if (httpStatus === 400 ||
        error.message?.includes('validation') ||
        error.message?.includes('invalid')) {
        return ErrorType.VALIDATION;
    }
    // Server errors (includes 502/503/504 from upstream)
    if ((httpStatus !== undefined && httpStatus >= 500) ||
        error.message?.includes('server') ||
        error.message?.includes('internal')) {
        return ErrorType.SERVER;
    }
    // Timeout errors
    if (error.code === 'TIMEOUT' ||
        error.message?.includes('timeout')) {
        return ErrorType.TIMEOUT;
    }
    return ErrorType.UNKNOWN;
};
/**
 * Determine if an error should be retried
 */
export const shouldRetryError = (error, attempt, maxRetries) => {
    if (attempt >= maxRetries)
        return false;
    const errorType = classifyError(error);
    // Don't retry validation or authorization errors
    if (errorType === ErrorType.VALIDATION || errorType === ErrorType.AUTHORIZATION) {
        return false;
    }
    // For network errors, only retry if device is online
    if (errorType === ErrorType.NETWORK) {
        if (!isOnline()) {
            // Device is offline, don't retry until online
            return false;
        }
        // Device is online, retry network errors
        return true;
    }
    // Retry server and timeout errors
    if (errorType === ErrorType.SERVER || errorType === ErrorType.TIMEOUT) {
        return true;
    }
    // Retry authentication errors only once (might be token refresh)
    if (errorType === ErrorType.AUTHENTICATION && attempt === 0) {
        return true;
    }
    return false;
};
/**
 * Calculate delay for exponential backoff
 */
export const calculateDelay = (attempt, baseDelay, maxDelay, backoffMultiplier) => {
    const delay = baseDelay * Math.pow(backoffMultiplier, attempt);
    return Math.min(delay, maxDelay);
};
/**
 * Enhanced retry function with comprehensive error handling
 */
export const handleDataPersistenceError = async (operation, retryFunction, options = {}) => {
    const { maxRetries = 3, baseDelay = 1000, maxDelay = 10000, backoffMultiplier = 2, shouldRetry = (error) => shouldRetryError(error, 0, maxRetries), onRetry, onSuccess, onFinalError } = options;
    const startTime = Date.now();
    let lastError = null;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            const result = await retryFunction();
            const totalTime = Date.now() - startTime;
            const retryResult = {
                success: true,
                data: result,
                attempts: attempt + 1,
                totalTime
            };
            onSuccess?.(result);
            return retryResult;
        }
        catch (error) {
            lastError = error;
            const errorType = classifyError(error);
            const isNetworkError = errorType === ErrorType.NETWORK;
            const isOffline = !isOnline();
            // Only log detailed errors for non-network errors or when offline
            if (!isNetworkError || !isOffline) {
                console.error(`❌ ${operation} failed on attempt ${attempt + 1}:`, {
                    error: error?.message || String(error),
                    type: errorType,
                    status: error?.response?.status,
                    code: error?.code
                });
            }
            else {
                // For network errors when offline, use a simpler log
                console.warn(`⚠️ ${operation} failed (offline):`, error?.message || 'Network unavailable');
            }
            // Check if we should retry
            if (attempt < maxRetries && shouldRetry(error)) {
                const delay = calculateDelay(attempt, baseDelay, maxDelay, backoffMultiplier);
                onRetry?.(attempt + 1, error);
                // For network errors, check if device came back online before retrying
                if (isNetworkError && isOffline) {
                    // Wait longer and check online status
                    await new Promise(resolve => setTimeout(resolve, delay * 2));
                    if (!isOnline()) {
                        // Still offline, don't retry
                        break;
                    }
                }
                else {
                    // Wait before retrying
                    await new Promise(resolve => setTimeout(resolve, delay));
                }
            }
            else {
                break;
            }
        }
    }
    const totalTime = Date.now() - startTime;
    const errorType = classifyError(lastError);
    const isNetworkError = errorType === ErrorType.NETWORK;
    // For network errors, use a less alarming message
    if (isNetworkError) {
        console.warn(`⚠️ ${operation} failed after ${maxRetries + 1} attempts (${totalTime}ms) - Network unavailable`, {
            error: lastError?.message || 'Network Error',
            code: lastError?.code,
            online: isOnline()
        });
    }
    else {
        console.error(`💥 ${operation} failed after ${maxRetries + 1} attempts (${totalTime}ms)`, lastError);
    }
    const retryResult = {
        success: false,
        error: lastError,
        attempts: maxRetries + 1,
        totalTime
    };
    onFinalError?.(lastError);
    return retryResult;
};
/**
 * Recovery strategies for different error types
 */
export const getRecoveryStrategy = (errorType) => {
    switch (errorType) {
        case ErrorType.NETWORK:
            return 'Check your internet connection and try again.';
        case ErrorType.AUTHENTICATION:
            return 'Please log in again to continue.';
        case ErrorType.AUTHORIZATION:
            return 'You do not have permission to perform this action.';
        case ErrorType.VALIDATION:
            return 'Please check your input and try again.';
        case ErrorType.SERVER:
            return 'Server error. Please try again later.';
        case ErrorType.TIMEOUT:
            return 'Request timed out. Please try again.';
        default:
            return 'An unexpected error occurred. Please try again.';
    }
};
/**
 * Enhanced error handler with user-friendly messages
 */
export const handleErrorWithUserFeedback = (error, operation, toast) => {
    const errorType = classifyError(error);
    const recoveryMessage = getRecoveryStrategy(errorType);
    console.error(`❌ ${operation} failed:`, {
        error: error.message || error,
        type: errorType,
        recovery: recoveryMessage
    });
    if (toast) {
        toast({
            title: `${operation} Failed`,
            description: recoveryMessage,
            variant: "destructive"
        });
    }
};
/**
 * Batch operation with error recovery
 */
export const handleBatchOperation = async (operations, options = {}) => {
    const results = [];
    for (let i = 0; i < operations.length; i++) {
        const operation = operations[i];
        const result = await handleDataPersistenceError(`Batch operation ${i + 1}/${operations.length}`, operation, options);
        results.push(result);
    }
    return results;
};
/**
 * Debounced retry function to prevent excessive retries
 */
export const createDebouncedRetry = (retryFunction, delay = 1000) => {
    let timeoutId = null;
    let isRetrying = false;
    return async () => {
        if (isRetrying)
            return;
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        return new Promise((resolve, reject) => {
            timeoutId = setTimeout(async () => {
                isRetrying = true;
                try {
                    const result = await retryFunction();
                    resolve(result);
                }
                catch (error) {
                    reject(error);
                }
                finally {
                    isRetrying = false;
                }
            }, delay);
        });
    };
};
/**
 * Circuit breaker pattern for preventing cascade failures
 */
export class CircuitBreaker {
    constructor(failureThreshold = 5, resetTimeout = 60000) {
        this.failureThreshold = failureThreshold;
        this.resetTimeout = resetTimeout;
        this.failures = 0;
        this.lastFailureTime = 0;
        this.state = 'CLOSED';
    }
    async execute(operation) {
        if (this.state === 'OPEN') {
            if (Date.now() - this.lastFailureTime > this.resetTimeout) {
                this.state = 'HALF_OPEN';
            }
            else {
                throw new Error('Circuit breaker is OPEN');
            }
        }
        try {
            const result = await operation();
            this.onSuccess();
            return result;
        }
        catch (error) {
            this.onFailure();
            throw error;
        }
    }
    onSuccess() {
        this.failures = 0;
        this.state = 'CLOSED';
    }
    onFailure() {
        this.failures++;
        this.lastFailureTime = Date.now();
        if (this.failures >= this.failureThreshold) {
            this.state = 'OPEN';
        }
    }
    getState() {
        return this.state;
    }
}
